import type { GroupType, OuKind } from '../types.ts'

export type DefaultParents = Partial<Record<`groups.${GroupType}` | 'users', string>>

export const DEFAULT_TARGET_LABELS: Record<keyof DefaultParents, string> = {
  'groups.AB': 'AB – Abteilungen',
  'groups.LW': 'LW – Laufwerke',
  'groups.FU': 'FU – Funktionen',
  'groups.SW': 'SW – Softwarezugriffe',
  'groups.LI': 'LI – Lizenzen',
  'groups.PR': 'PR – Printer',
  'groups.SA': 'SA – SysAdmin',
  users: 'Users (Standard-Benutzer)',
}

export const DEFAULT_TARGET_KEYS: Array<keyof DefaultParents> = [
  'groups.AB', 'groups.LW', 'groups.FU', 'groups.SW',
  'groups.LI', 'groups.PR', 'groups.SA', 'users',
]

export const SHORT_BADGE: Record<keyof DefaultParents, string> = {
  'groups.AB': 'AB', 'groups.LW': 'LW', 'groups.FU': 'FU', 'groups.SW': 'SW',
  'groups.LI': 'LI', 'groups.PR': 'PR', 'groups.SA': 'SA', users: 'User',
}

export interface ExplorerNode {
  key: string
  kind: 'ou' | 'group' | 'user' | 'orphan'
  id: string
  name: string
  description?: string
  ouKind?: OuKind
  isReadOnly?: boolean
  /** Welche Default-Targets zeigen auf diese OU (nur kind='ou') */
  defaultTargets?: Array<keyof DefaultParents>
  groupType?: string
  children: ExplorerNode[]
}

/** Einheitliches Eingabeformat fuer buildTree – beide Explorer konvertieren ihre Daten hierauf */
export interface TreeBuildItem {
  id: string
  kind: 'ou' | 'group' | 'user'
  name: string
  description?: string
  /** Fuer OUs: ID der Parent-OU (null = Top-Level) */
  parentId?: string | null
  /** Fuer Groups/Users: ID der Ziel-OU (explizit zugewiesen) */
  ouId?: string
  groupType?: string
  isReadOnly?: boolean
  ouKind?: OuKind
}

/**
 * Baut den ExplorerNode-Baum aus einem flachen TreeBuildItem-Array.
 * Gruppen/Users ohne explizite ouId fallen auf defaultParents zurueck.
 * Items ohne aufloesbare OU landen im Orphan-Knoten.
 */
export function buildTree(items: TreeBuildItem[], defaultParents: DefaultParents = {}): ExplorerNode[] {
  const ous = items.filter((i) => i.kind === 'ou')
  const groups = items.filter((i) => i.kind === 'group')
  const users = items.filter((i) => i.kind === 'user')

  const ouIds = new Set(ous.map((o) => o.id))

  // defaultsByOuId: ouId → Targets die auf diese OU zeigen (fuer Badges)
  const defaultsByOuId = new Map<string, Array<keyof DefaultParents>>()
  for (const key of DEFAULT_TARGET_KEYS) {
    const ouId = defaultParents[key]
    if (!ouId) continue
    const list = defaultsByOuId.get(ouId) ?? []
    list.push(key)
    defaultsByOuId.set(ouId, list)
  }

  // OUs nach Parent gruppieren
  const ousByParent = new Map<string | null, TreeBuildItem[]>()
  for (const ou of ous) {
    const parentId = ou.parentId && ouIds.has(ou.parentId) ? ou.parentId : null
    const list = ousByParent.get(parentId) ?? []
    list.push(ou)
    ousByParent.set(parentId, list)
  }
  for (const list of ousByParent.values()) {
    list.sort((a, b) => a.name.localeCompare(b.name, 'de'))
  }

  // Groups nach Ziel-OU (explizit oder Default)
  const groupsByOu = new Map<string, TreeBuildItem[]>()
  const orphanGroups: TreeBuildItem[] = []
  for (const g of groups) {
    const explicitId = g.ouId && ouIds.has(g.ouId) ? g.ouId : null
    const defaultId = g.groupType
      ? defaultParents[`groups.${g.groupType}` as keyof DefaultParents]
      : undefined
    const targetId = explicitId ?? (defaultId && ouIds.has(defaultId) ? defaultId : null)
    if (!targetId) { orphanGroups.push(g); continue }
    const list = groupsByOu.get(targetId) ?? []
    list.push(g)
    groupsByOu.set(targetId, list)
  }
  for (const list of groupsByOu.values()) {
    list.sort((a, b) => {
      if (a.groupType !== b.groupType) return (a.groupType ?? '').localeCompare(b.groupType ?? '')
      return a.name.localeCompare(b.name, 'de')
    })
  }

  // Users nach Ziel-OU (explizit oder Default)
  const usersByOu = new Map<string, TreeBuildItem[]>()
  const orphanUsers: TreeBuildItem[] = []
  for (const u of users) {
    const explicitId = u.ouId && ouIds.has(u.ouId) ? u.ouId : null
    const defaultId = defaultParents.users
    const targetId = explicitId ?? (defaultId && ouIds.has(defaultId) ? defaultId : null)
    if (!targetId) { orphanUsers.push(u); continue }
    const list = usersByOu.get(targetId) ?? []
    list.push(u)
    usersByOu.set(targetId, list)
  }
  for (const list of usersByOu.values()) {
    list.sort((a, b) => a.name.localeCompare(b.name, 'de'))
  }

  function toOuNode(ou: TreeBuildItem): ExplorerNode {
    return {
      key: `ou:${ou.id}`,
      kind: 'ou',
      id: ou.id,
      name: ou.name,
      description: ou.description,
      ouKind: ou.ouKind,
      isReadOnly: ou.isReadOnly,
      defaultTargets: defaultsByOuId.get(ou.id),
      children: [
        ...(ousByParent.get(ou.id) ?? []).map(toOuNode),
        ...(groupsByOu.get(ou.id) ?? []).map<ExplorerNode>((g) => ({
          key: `group:${g.id}`,
          kind: 'group',
          id: g.id,
          name: g.name,
          description: g.description,
          groupType: g.groupType,
          isReadOnly: g.isReadOnly,
          children: [],
        })),
        ...(usersByOu.get(ou.id) ?? []).map<ExplorerNode>((u) => ({
          key: `user:${u.id}`,
          kind: 'user',
          id: u.id,
          name: u.name,
          description: u.description,
          isReadOnly: u.isReadOnly,
          children: [],
        })),
      ],
    }
  }

  const top = (ousByParent.get(null) ?? []).map(toOuNode)

  if (orphanGroups.length + orphanUsers.length > 0) {
    top.push({
      key: '__orphan__',
      kind: 'orphan',
      id: '__orphan__',
      name: 'Ohne OU-Zuordnung',
      description: 'Items ohne gültige OU-Zuordnung — Default-Speicherort konfigurieren oder OU direkt zuweisen.',
      children: [
        ...orphanGroups.map<ExplorerNode>((g) => ({
          key: `group:${g.id}`, kind: 'group', id: g.id, name: g.name,
          description: g.description, groupType: g.groupType, isReadOnly: g.isReadOnly, children: [],
        })),
        ...orphanUsers.map<ExplorerNode>((u) => ({
          key: `user:${u.id}`, kind: 'user', id: u.id, name: u.name,
          description: u.description, isReadOnly: u.isReadOnly, children: [],
        })),
      ],
    })
  }

  return top
}
